<template>
  <div v-if="detail" class="location-about-tab">
    <template v-if="hasOpenHours">
      <span class="section-title">{{ localize('locations.detail.about.openingHours.title') }}</span>
      <hr class="section-line" />
      <div class="open-hours">
        <div v-for="(workHours, d) in sortedDays" :key="`day-${d}`" :class="['day', `day-${d}`]">
          <span class="name">{{ dayName(workHours.day) }}</span>

          <span v-if="!workHours.start || !workHours.end" class="closed">Closed</span>
          <template v-else>
            <span class="time">{{ workHours.start }}</span>
            <span class="delimiter">&mdash;</span>
            <span class="time">{{ workHours.end }}</span>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { DetailLocationDto } from '@/core/data/location/location.interface';
import { localize } from '@/core/gambits';
import { computed } from 'vue';

interface Props {
  detail: DetailLocationDto;
}

const { detail } = defineProps<Props>();

const hasOpenHours = computed(() => {
  return detail.openingHours !== null;
});

function dayName(d: number) {
  switch (d) {
    case 0:
      return 'Sunday';
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thursday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';
    default:
      return '';
  }
}

const schedule = computed(() => {
  return [
    { day: 0, start: getStart(0), end: getEnd(0) },
    { day: 1, start: getStart(1), end: getEnd(1) },
    { day: 2, start: getStart(2), end: getEnd(2) },
    { day: 3, start: getStart(3), end: getEnd(3) },
    { day: 4, start: getStart(4), end: getEnd(4) },
    { day: 5, start: getStart(5), end: getEnd(5) },
    { day: 6, start: getStart(6), end: getEnd(6) },
  ];
});

const sortedDays = computed(() => {
  const today = new Date().getDay();

  return [...schedule.value].sort((a, b) => {
    const aOffset = (a.day - today + 7) % 7;
    const bOffset = (b.day - today + 7) % 7;
    return aOffset - bOffset;
  });
});

function getStart(d: number) {
  return (detail.openingHours || []).find((s) => s.day === d)?.start || null;
}

function getEnd(d: number) {
  return (detail.openingHours || []).find((s) => s.day === d)?.end || null;
}
</script>

<style lang="scss" scoped>
.location-about-tab {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  width: 100%;
}

.section-line {
  border: none;
  border-top: 1px solid var(--color-white-10);
  width: 100%;
  margin: 0 0 0.75rem;
}

.section-title {
  display: block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-white);
  margin-bottom: 0.75rem;
}

.open-hours {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.75rem;
}

.open-hours .day {
  display: flex;
  align-items: center;
  color: var(--color-white-70);
  font-size: 14px;
  letter-spacing: 0.05em;
}

.open-hours .name {
  width: 8rem;
  flex-shrink: 0;
  color: var(--color-white-40);
}

.open-hours .time {
  letter-spacing: 0.1em;
}

.open-hours .closed {
  color: var(--color-white-40);
  text-transform: uppercase;
  letter-spacing: 0.2em;
  font-size: 11px;
}

.open-hours .day-0 {
  & > span {
    color: var(--color-white) !important;
    font-weight: 600;
  }
}

.open-hours .day-6 {
  opacity: 0.5;
}

.open-hours .day-5 {
  opacity: 0.6;
}

.open-hours .day-4 {
  opacity: 0.7;
}

.open-hours .day-3 {
  opacity: 0.8;
}

.open-hours .day-2 {
  opacity: 0.9;
}

.open-hours .day-1 {
  opacity: 1;
}
</style>
